import { Mutable } from '@evorait/pace-core';
import { on } from '@ngrx/store';
import moment from 'moment';
import { FormParticipantCopies } from '../../../types/form-participant-copies';
import { FormsConstants } from '../../../types/forms-constants';
import { ParticipantCopyId } from '../../../types/participant-copy';
import { Recipient } from '../../../types/recipient';
import { FormUtilities } from '../../../utilities/form-utilities';
import { setOrgData } from '../../actions/customer-identification.actions';
import {
    abortForm,
    loadForm,
    loadFormError,
    loadFormSuccess,
    markFormAsEdited,
    navigateToForm,
    saveForm,
    saveFormSuccess,
    saveRecipients,
    submitFormSuccess,
    unloadForm,
    updatedXml,
    updateFormReadOnlyReason,
} from '../../actions/form.actions';
import { loadSpqDoc } from '../../actions/questionnaire.actions';
import { FormState, initialFormState } from '../../state/form.state';
import { handOverUnsubmittedFormsSuccess } from '../../actions/state.actions';

export function navigateToFormReducer(state: Readonly<FormState>): Readonly<FormState> {
    // reset the error state so that we can try again to load a form and pass the router activation guard
    return { ...state, errorDuringLoading: false };
}

export function loadFormReducer(state: Readonly<FormState>): Readonly<FormState> {
    return { ...state, isLoading: true };
}

export function loadFormSuccessReducer(
    state: Readonly<FormState>,
    props: { form: Readonly<FormState> },
): Readonly<FormState> {
    return {
        ...initialFormState,
        ...props.form,
        isLoading: false,
        errorDuringLoading: false,
        loadingStatus: FormsConstants.FORM_LOADING_STATUS.LOADED,
    };
}

export function loadFormErrorReducer(state: Readonly<FormState>): Readonly<FormState> {
    return {
        ...state,
        isLoading: false,
        errorDuringLoading: true,
        loadingStatus: FormsConstants.FORM_LOADING_STATUS.UNLOADED,
    };
}

export function unloadFormReducer(state: Readonly<FormState>): Readonly<FormState> {
    return { ...state, loadingStatus: FormsConstants.FORM_LOADING_STATUS.UNLOADED };
}

export function saveFormReducer(state: Readonly<FormState>): Readonly<FormState> {
    return { ...state, isSaving: true };
}

export function saveFormSuccessReducer(state: Readonly<FormState>): Readonly<FormState> {
    return { ...state, isSaving: false, loadingStatus: FormsConstants.FORM_LOADING_STATUS.SAVED };
}

export function abortFormReducer(state: Readonly<FormState>): Readonly<FormState> {
    return { ...state, loadingStatus: FormsConstants.FORM_LOADING_STATUS.UNLOADED };
}

export function submitFormSuccessReducer(
    state: Readonly<FormState>,
    props: {
        ids: ParticipantCopyId[];
        skipFormListProcessing: boolean;
    },
): Readonly<FormState> {
    if (FormUtilities.isServicePartQuestionnaire(state.type)) {
        return { ...state };
    }

    const newParticipantCopies: Mutable<Omit<FormParticipantCopies, 'master'>> = {};

    props.ids.forEach((id) => {
        newParticipantCopies[id] = {
            ...state.participantCopies[id],
            submittedOn: moment().toISOString(),
        };
    });

    return {
        ...state,
        participantCopies: {
            ...state.participantCopies,
            ...newParticipantCopies,
        },
    };
}

export function saveRecipientsReducer(
    state: Readonly<FormState>,
    props: { recipients: Recipient[] },
): Readonly<FormState> {
    return {
        ...state,
        recipients: [...props.recipients],
    };
}

export function updatedXmlReducer(
    state: Readonly<FormState>,
    props: { xml: string; participant: ParticipantCopyId },
): Readonly<FormState> {
    return {
        ...state,
        participantCopies: {
            ...state.participantCopies,
            [props.participant]: {
                ...state.participantCopies[props.participant],
                xml: props.xml,
            },
        },
    };
}

export function setOrgDataReducer(
    state: Readonly<FormState>,
    props: {
        product: string;
        productType: string;
        variant: string;
        productKey: string;
        doctypeKey: string;
        modality: string;
        defaultInfoClass: string;
        version: string;
    },
): Readonly<FormState> {
    return {
        ...state,
        orgData: {
            product: props.product,
            productType: props.productType,
            variant: props.variant,
            productKey: props.productKey,
            doctypeKey: props.doctypeKey,
            modality: props.modality,
            defaultInfoClass: props.defaultInfoClass,
            version: props.version,
        },
    };
}

export function markFormAsEditedReducer(state: Readonly<FormState>): Readonly<FormState> {
    if (state.loadingStatus === FormsConstants.FORM_LOADING_STATUS.EDITED) {
        return state;
    }

    return {
        ...state,
        loadingStatus: FormsConstants.FORM_LOADING_STATUS.EDITED,
    };
}

export function updateFormReadOnlyReasonReducer(
    state: Readonly<FormState>,
    props: { readonlyReason: FormsConstants.READONLY_REASON },
): Readonly<FormState> {
    return {
        ...state,
        readOnlyReason: props.readonlyReason,
    };
}

export const formOperationsReducer = [
    on<FormState, Array<typeof navigateToForm>>(navigateToForm, navigateToFormReducer),
    on<FormState, Array<typeof loadForm>>(loadForm, loadFormReducer),
    on<FormState, Array<typeof loadSpqDoc>>(loadSpqDoc, loadFormReducer),
    on<FormState, Array<typeof loadFormSuccess>>(loadFormSuccess, loadFormSuccessReducer),
    on<FormState, Array<typeof unloadForm>>(unloadForm, unloadFormReducer),
    on<FormState, Array<typeof loadFormError>>(loadFormError, loadFormErrorReducer),
    on<FormState, Array<typeof saveForm>>(saveForm, saveFormReducer),
    on<FormState, Array<typeof saveFormSuccess>>(saveFormSuccess, saveFormSuccessReducer),
    on<FormState, Array<typeof abortForm>>(abortForm, abortFormReducer),
    on<FormState, Array<typeof submitFormSuccess>>(submitFormSuccess, submitFormSuccessReducer),
    on<FormState, Array<typeof saveRecipients>>(saveRecipients, saveRecipientsReducer),
    on<FormState, Array<typeof updatedXml>>(updatedXml, updatedXmlReducer),
    on<FormState, Array<typeof setOrgData>>(setOrgData, setOrgDataReducer),
    on<FormState, Array<typeof markFormAsEdited>>(markFormAsEdited, markFormAsEditedReducer),
    on<FormState, Array<typeof updateFormReadOnlyReason>>(updateFormReadOnlyReason, updateFormReadOnlyReasonReducer),
    on<FormState, Array<typeof handOverUnsubmittedFormsSuccess>>(
        handOverUnsubmittedFormsSuccess,
        () => initialFormState,
    ),
];
